import Button from 'components/ui/Button'
import CartPrices from 'components/CartPrices'
import { CartPage } from 'components/pages/CartPage/types/query'
import CartTipSelector from 'components/pages/CartPage/CartTipSelector'
import TimePicker from 'components/pages/CartPage/TimePicker'
import CustomerNotes from 'components/pages/CartPage/CustomerNotes'
import Coupon from 'components/pages/CartPage/Coupon'
import ButtonLink from 'components/ui/ButtonLink'
import { ArrowRightCircleIcon, PlusIcon } from '@heroicons/react/24/solid'
import LocationNotice from 'components/LocationNotice'
import useUser from 'queries/useUser'
import { useRouter } from 'next/router'
import { useCartCheckoutSession } from 'components/pages/CartPage/Checkout/types/cartCheckoutSession'

import { useBlockUi } from 'components/BlockUi/useBlockUi'
import getMutationErrors from 'utils/getMutationErrors'
import { MutationResult } from '@apollo/client'
import { FORM_ERROR } from 'final-form'
import { useEffect, useState } from 'react'
import Notification from 'components/Notification'
import smoothVerticalScrolling from 'utils/smoothScrolling'
import CompleteProfileModal from 'components/CompleteProfileModal'

import { logEvent } from 'utils/analytics'

type Props = {
  cart: NonNullable<CartPage['currentCart']>
  cartRefetch: () => void
}

const Checkout = ({ cart, cartRefetch }: Props) => {
  const router = useRouter()
  const blockUi = useBlockUi()

  const [checkoutError, setCheckoutError] = useState<string | null>()
  const [couponError, setCouponError] = useState<string | null>()
  const [checkoutWarning, setCheckoutWarning] = useState<string | null>()
  const [pickupTimeError, setPickupTimeError] = useState<string | null>()
  const [tipError, setTipError] = useState<string | null>()
  const [showCompleteProfile, setShowCompleteProfile] = useState<boolean>(false)

  const { oldTotal, checkout: autoCheckout } = router.query as {
    oldTotal?: string
    checkout?: string
  }

  const { user } = useUser({ skip: typeof window === 'undefined' })

  const [cartCheckoutSession] = useCartCheckoutSession({})

  const onCheckout = async ({ auto }: { auto: boolean }) => {
    setTipError(null)
    setCheckoutError(null)
    setCouponError(null)
    setCheckoutWarning(null)
    setPickupTimeError(null)

    if (
      cart.location!.tipsEnabled &&
      !(cart.tipPercentage || cart.tipPercentage === 0) &&
      !cart.amountCustomTip
    ) {
      const element = document.getElementById('tipSelector')
      if (element) {
        smoothVerticalScrolling({ element, offset: 90 })
      }
      return setTipError('Please select how much you want to tip')
    }

    if (!cart.pickupTimeSelected) {
      const element = document.getElementById('pickupTimeSelector')
      if (element) {
        smoothVerticalScrolling({ element, offset: 90 })
      }
      return setPickupTimeError('Please select a pickup time')
    }

    logEvent('begin_checkout', {
      value: cart.price.total.cents / 100,
      currency: 'USD',
      coupon: cart.coupon?.code,
      items: cart.items.map((item) => ({
        id: item.locationItem.item.id,
        name: item.locationItem.item.name
      }))
    })

    if (!user) {
      return router.push(
        `/customers/login?redirect_to=${encodeURIComponent(
          `/cart?checkout=true&oldTotal=${cart.price.total.cents}`
        )}`
      )
    }
    if (!user.completeProfile) {
      return setShowCompleteProfile(true)
    }

    const response = await blockUi.withBlockUi(() =>
      cartCheckoutSession({
        variables: {
          cartUuid: cart.uuid!,
          priceCents:
            oldTotal && auto ? parseInt(oldTotal, 10) : cart.price.total.cents
        }
      })
    )

    const errors:
      | undefined
      | {
          tip?: string
          pickupTime?: string
          availableItems?: string
          price?: string
          [FORM_ERROR]?: string
        } = getMutationErrors(response! as MutationResult, [
      'tip',
      'pickupTime',
      'availableItems',
      'price'
    ])

    if (errors) {
      await cartRefetch()

      if (errors.pickupTime) {
        setPickupTimeError(errors.pickupTime)
        const element = document.getElementById('pickupTimeSelector')
        if (element) {
          smoothVerticalScrolling({ element, offset: 90 })
        }
      } else if (errors.tip) {
        setTipError(errors.tip)
        const element = document.getElementById('tipSelector')
        if (element) {
          smoothVerticalScrolling({ element, offset: 90 })
        }
      } else if (errors.availableItems) {
        setCheckoutError(errors.availableItems)
      } else if (errors.price) {
        setCheckoutWarning(errors.price)
      } else if (errors[FORM_ERROR]) {
        setCheckoutError(errors[FORM_ERROR])
      }
      return null
    }

    const { order, stripeCheckoutUrl } = response!.data!.cartCheckoutSession!

    if (order) {
      return router.push(`/profile/orders/${order.id}?success=true`)
    }
    if (stripeCheckoutUrl) {
      window.location.href = stripeCheckoutUrl
      return
    }
    throw new Error('No session or order was returned')
  }

  useEffect(() => {
    if (autoCheckout && user) {
      onCheckout({ auto: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCheckout, user])

  const canOrder =
    cart.location?.workStatus === 'pre_order' ||
    cart.location?.workStatus === 'work_started'

  return (
    <div className="mt-4 lg:mt-0 relative">
      <div className="bg-gray-100 rounded-xl h-full md:border border-solid border-gray-100 lg:mb-0 mb-24 py-6 px-4">
        <div className="space-y-6">
          {cart.location!.tipsEnabled && (
            <CartTipSelector
              currentAmountCustomTip={cart.amountCustomTip?.cents}
              currentTipPercentage={cart.tipPercentage}
              error={tipError}
              refetch={cartRefetch}
              resetTipError={() => setTipError(null)}
              uuid={cart.uuid!}
            />
          )}
          <CustomerNotes
            cartRefetch={cartRefetch}
            defaultCustomerNotes=""
            uuid={cart.uuid!}
          />
          <TimePicker
            asapOrdersEnabled={cart.location!.asapOrdersEnabled}
            cartRefetch={cartRefetch}
            delayedPickupEnabled={cart.location!.delayedPickupEnabled}
            error={pickupTimeError}
            locationAvailableOrderTimes={cart.location!.availableOrderTimes}
            pickupTimeSelected={cart.pickupTimeSelected}
            resetPickupTimeError={() => setPickupTimeError(null)}
            selectedValue={cart.delayedPickupAt}
            uuid={cart.uuid!}
          />
          <div className="space-y-2">
            <CartPrices lines={cart.price.lines} />

            <Coupon
              currentCoupon={cart.coupon?.code}
              refetch={cartRefetch}
              setError={setCouponError}
              uuid={cart.uuid!}
            />
          </div>

          {couponError && (
            <Notification variant="error">{couponError}</Notification>
          )}

          <LocationNotice location={cart.location!} />

          {checkoutError && (
            <Notification variant="error">{checkoutError}</Notification>
          )}
          {checkoutWarning && (
            <Notification variant="warning">{checkoutWarning}</Notification>
          )}

          {!canOrder && (
            <div>
              <ButtonLink
                color="default"
                href={cart.location!.lot.lotUrl}
                size="large"
                variant="contained"
                fullWidth
              >
                Browse other trucks
                <span>
                  <ArrowRightCircleIcon className="h-6 w-6 ml-2" />
                </span>
              </ButtonLink>
            </div>
          )}
          {canOrder && (
            <div className="space-y-4 ">
              <ButtonLink
                color="default"
                href={cart.location!.customerUrl}
                size="large"
                variant="outline"
                fullWidth
              >
                <span>
                  <PlusIcon className="h-6 w-6" />
                </span>
                Add more items
              </ButtonLink>

              <div className="hidden lg:block">
                <Button
                  color="primary"
                  onClick={() => onCheckout({ auto: false })}
                  size="large"
                  variant="contained"
                  fullWidth
                >
                  Checkout
                  <span>
                    <ArrowRightCircleIcon className="h-6 w-6 ml-2 text-orange-100 group-hover:text-orange-50 transition-all duration-200" />
                  </span>
                </Button>
              </div>

              <div className="lg:hidden block">
                <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 border-solid pt-2 px-4 pb-4">
                  <div className="max-w-2xl mx-auto">
                    <Button
                      color="primary"
                      onClick={() => onCheckout({ auto: false })}
                      size="large"
                      variant="contained"
                      fullWidth
                    >
                      Checkout
                      <span>
                        <ArrowRightCircleIcon className="h-6 w-6 ml-2 text-orange-100 group-hover:text-orange-50 transition-all duration-200" />
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showCompleteProfile && (
        <CompleteProfileModal
          onClose={() => setShowCompleteProfile(false)}
          onComplete={() => {
            setShowCompleteProfile(false)
            onCheckout({ auto: true })
          }}
          open={showCompleteProfile}
        />
      )}
    </div>
  )
}

export default Checkout
